import React, { useState, useEffect } from "react";
import { FaArrowLeft, FaPlus, FaTrash, FaShoppingCart } from "react-icons/fa";
import axios from 'axios';
import axiosInstance from '../../../api/axiosInstance';
import { getMenuByRestaurant } from '../../../api/menuApi';
import LoadingButton from "../../LoadingButton";
import { CustomerDetails } from "../types";

export type CartItem = {
  id: string;
  name: string;
  price: number;
  quantity: number;
  instructions?: string;
};

type Props = {
  cartItems: CartItem[];
  restaurantGuid: string;
  tableId: string;
  customerDetails: CustomerDetails;
  onBack: () => void;
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  onUpdateInstructions: (itemId: string, instructions: string) => void;
  onRemoveItem: (itemId: string) => void;
  onPlaceOrder: () => void;
};

const CartPage: React.FC<Props> = ({
  cartItems,
  restaurantGuid,
  tableId,
  customerDetails,
  onBack,
  onUpdateQuantity,
  onUpdateInstructions,
  onRemoveItem,
  onPlaceOrder,
}) => {
  const [editingInstructions, setEditingInstructions] = useState<string | null>(null);
  const [instructionText, setInstructionText] = useState("");
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [menu, setMenu] = useState<any[]>([]);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);

  const total = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  // Load Menu from cache or API
  useEffect(() => {
    const stored = localStorage.getItem('menu');
    if (stored) {
      setMenu(JSON.parse(stored));
    } else {
      getMenuByRestaurant(restaurantGuid)
        .then(menuData => {
          setMenu(menuData);
          localStorage.setItem('menu', JSON.stringify(menuData));
        })
        .catch(err => console.error('Error loading menu:', err));
    }
  }, [restaurantGuid]);

  const handlePlaceOrder = async () => {
    if (isPlacingOrder) return; // prevent double-click while already placing
    setIsPlacingOrder(true);
    setError('');
    setSuccess('');

    if (cartItems.length === 0) {
      setError('Your cart is empty. Please add items before placing an order.');
      setIsPlacingOrder(false);
      return;
    }

    try {
      const payload = {
        restaurantGuid,
        placedBy: '',
        tableId: tableId.toString(),
        customerId: customerDetails.id || '',
        orderType: 'DINE_IN',
        orderSource: 'QUICK_SERVE',
        items: cartItems.map(item => {
          const menuItem = menu.find(m => m.id === item.id);
          return {
            menuItemId: item.id,
            quantity: item.quantity,
            price: menuItem?.price || null,
            discountedPrice: menuItem?.discountPrice || null,
            cookingInstructions: item.instructions,
            estimatedTimeInMin: menuItem?.estimatedTimeInMin || null
          };
        }),
      };

      await axiosInstance.post(`/Order/PlaceOrder`, payload);

      setSuccess('Order placed successfully!');

      // Clear cart and navigate after a short delay
      setTimeout(() => {
        setSuccess(''); // Close the success modal
        onPlaceOrder(); // This should clear cart and navigate
      }, 2000); // Wait 2 seconds before navigating and closing modal

    } catch (err) {
      console.error('Failed to place order:', err);
      if (axios.isAxiosError(err) && err.response) {
        setError(err.response.data.message || 'Failed to place order.');
      } else {
        setError('Failed to place order. Please try again.');
      }

      // Clear the error message after a delay
      setTimeout(() => {
        setError('');
      }, 3000);
    } finally {
      setIsPlacingOrder(false); // stop loader
    }
  };

  const handleEditInstructions = (itemId: string, currentInstructions: string) => {
    setEditingInstructions(itemId);
    setInstructionText(currentInstructions || "");
  };

  const handleSaveInstructions = (itemId: string) => {
    onUpdateInstructions(itemId, instructionText);
    setEditingInstructions(null);
    setInstructionText("");
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      {/* Header */}
      <header className="sticky top-0 z-20 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 py-2 sm:py-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={onBack}
              className="p-1.5 sm:p-2 rounded-lg transition-colors duration-200 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
              title="Back"
            >
              <FaArrowLeft size={16} className="sm:w-5 sm:h-5" />
            </button>
            <div>
              <h1 className="text-lg sm:text-xl md:text-2xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-amber-600 dark:from-orange-400 dark:to-amber-500">
                Cart
              </h1>
              <p className="text-xs sm:text-sm mt-1 text-gray-600 dark:text-gray-400">
                {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Cart Items */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 py-4">
        {cartItems.length === 0 ? (
          <div className="text-center py-12">
            <FaShoppingCart size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-lg text-gray-600 dark:text-gray-400">Your cart is empty</p>
          </div>
        ) : (
          <div className="space-y-4">
            {cartItems.map((item) => (
              <div
                key={item.id}
                className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm"
              >
                {/* Main layout */}
                <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1">

                  {/* LEFT COLUMN */}
                  <div className="flex flex-col gap-1">

                    {/* Item name */}
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white leading-snug">
                      {item.name}
                    </h3>

                    {/* Price × Qty */}
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      ₹{item.price} × {item.quantity}
                    </div>

                    {/* Cooking request */}
                    {editingInstructions !== item.id && (
                      <button
                        onClick={() =>
                          handleEditInstructions(item.id, item.instructions || "")
                        }
                        className="flex items-center gap-1.5 text-orange-500 text-xs font-semibold w-fit hover:underline"
                      >
                        <FaPlus size={10} />
                        {item.instructions ? "Edit instructions" : "Add instructions"}
                      </button>
                    )}
                  </div>

                  {/* RIGHT COLUMN */}
                  <div className="flex flex-col items-end justify-between">

                    {/* Delete */}
                    <button
                      onClick={() => onUpdateQuantity(item.id, 0)}
                      className="text-red-500 hover:text-red-600 p-1 rounded-full"
                      aria-label="Remove item"
                    >
                      <FaTrash size={14} />
                    </button>

                    {/* Quantity Controls (centered relative to left content) */}
                    <div className="flex items-center bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-full h-9 px-1 mt-2">
                      <button
                        onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                        disabled={item.quantity === 1}
                        className="w-9 h-9 flex items-center justify-center text-lg font-semibold
                       text-red-500 hover:bg-red-50 dark:hover:bg-gray-600
                       rounded-full disabled:opacity-40"
                      >
                        −
                      </button>

                      <span className="w-7 text-center text-sm font-semibold text-gray-900 dark:text-white">
                        {item.quantity}
                      </span>

                      <button
                        onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                        className="w-9 h-9 flex items-center justify-center text-lg font-semibold
                       text-green-600 hover:bg-green-50 dark:hover:bg-gray-600
                       rounded-full"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
                {/* Instruction editor / preview (full width below) */}
                {editingInstructions === item.id && (
                  <div className="mt-3">
                    <input
                      type="text"
                      value={instructionText}
                      onChange={(e) => setInstructionText(e.target.value)}
                      placeholder="Add cooking instructions"
                      className="w-full px-3 py-2 text-sm rounded-lg
                     border border-gray-300 dark:border-gray-600
                     bg-gray-50 dark:bg-gray-700
                     text-gray-900 dark:text-white
                     focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />

                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => handleSaveInstructions(item.id)}
                        className="flex-1 py-1.5 bg-orange-500 text-white rounded-lg text-xs hover:bg-orange-600"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingInstructions(null)}
                        className="flex-1 py-1.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-xs"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                {/* Saved instruction preview */}
                {item.instructions && editingInstructions !== item.id && (
                  <p className="mt-2 text-xs italic text-gray-500 dark:text-gray-400">
                    “{item.instructions}”
                  </p>
                )}
              </div>
            ))}
            {/* Order Summary */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
              <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-3">Order Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-700 text-base font-bold text-orange-600 dark:text-orange-400">
                  <span>Total</span>
                  <span>₹{total.toFixed(2)}</span>
                </div>
              </div>
            </div>
            {/* Place Order Button */}
            <LoadingButton
              text="Place Order"
              loadingText="Placing..."
              loading={isPlacingOrder}
              onClick={handlePlaceOrder}
            />
          </div>
        )}
      </div>

      {/* Success/Error Modals */}
      {success && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white p-8 rounded-xl shadow-2xl text-center max-w-sm w-full mx-4">
            <div className="text-green-500 text-6xl mb-4">✓</div>
            <h4 className="text-2xl font-bold text-gray-900">Order Placed!</h4>
            <p className="mt-2 text-gray-600">{success}</p>
          </div>
        </div>
      )}
      {error && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white p-8 rounded-xl shadow-2xl text-center max-w-sm w-full mx-4">
            <div className="text-red-500 text-6xl mb-4">✕</div>
            <h4 className="text-2xl font-bold text-gray-900">Order Failed</h4>
            <p className="mt-2 text-gray-600">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;